<template>
  <div class="modal" :class="{ 'is-active': modelValue }">
    <div 
      class="modal-background" 
      @click="handleBackgroundClick"
    ></div>
    <div class="modal-card" :class="[sizeClass, { 'is-quiet': quiet }]">
      <header class="modal-card-head" :class="headerClass">
        <slot name="header">
          <div class="modal-card-head-text">
            <div class="modal-card-title" :class="titleClass">{{ title }}</div>
            <p v-if="subtitle" class="modal-card-subtitle">{{ subtitle }}</p>
          </div>
        </slot>
        <button
          v-if="!$slots.header && quiet"
          type="button"
          class="modal-close-btn"
          title="Close (Esc)"
          aria-label="Close"
          @click="close"
        >
          <kbd>ESC</kbd>
        </button>
        <button
          v-else-if="!$slots.header"
          class="delete"
          aria-label="close"
          @click="close"
        ></button>
      </header>
      
      <section class="modal-card-body">
        <slot>
          <!-- Default slot for body content -->
        </slot>
      </section>
      
      <footer v-if="!hideCancel || !hideConfirm || $slots['footer-left']" class="modal-card-foot">
        <!-- Left side slot for custom content -->
        <div class="footer-left">
          <slot name="footer-left"></slot>
        </div>
        
        <!-- Right side with consistent button layout -->
        <div class="footer-right ml-auto">
          <!-- Cancel button (always is-text, always on left of primary) -->
          <button 
            v-if="!hideCancel"
            class="button is-text has-text-grey" 
            @click="cancel"
            :disabled="loading"
          >
            {{ cancelText }}
          </button>
          
          <!-- Primary action button (themed based on modal state) -->
          <button 
            v-if="!hideConfirm"
            class="button ml-2" 
            :class="confirmButtonClass"
            :disabled="confirmDisabled || loading"
            @click="confirm"
          >
            <span v-if="loading" class="icon">
              <font-awesome-icon :icon="['fas', 'spinner']" spin />
            </span>
            <span>{{ confirmText }}</span>
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  // v-model binding
  modelValue: {
    type: Boolean,
    default: false
  },
  
  // Modal content
  title: {
    type: String,
    default: ''
  },
  // Quiet line under the title (e.g. who/what the modal acts on)
  subtitle: {
    type: String,
    default: ''
  },
  // White header with a hairline instead of the theme-colored bar
  quiet: {
    type: Boolean,
    default: false
  },
  
  // Theme affects header color and confirm button
  theme: {
    type: String,
    default: 'primary',
    validator: (value) => {
      return ['primary', 'danger', 'warning', 'info', 'success', 'dark', 'light'].includes(value);
    }
  },
  
  // Size variants
  size: {
    type: String,
    default: 'medium',
    validator: (value) => {
      return ['small', 'medium', 'large', 'fullscreen'].includes(value);
    }
  },
  
  // Button text
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  
  // Button visibility
  hideCancel: {
    type: Boolean,
    default: false
  },
  hideConfirm: {
    type: Boolean,
    default: false
  },
  
  // Button states
  confirmDisabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  
  // Behavior
  closeOnBackground: {
    type: Boolean,
    default: true
  },
  closeOnEsc: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'close']);

// Computed classes
const sizeClass = computed(() => {
  const sizeMap = {
    small: 'is-small',
    medium: '',
    large: 'is-large',
    fullscreen: 'is-fullscreen'
  };
  return sizeMap[props.size] || '';
});

const headerClass = computed(() => {
  if (props.quiet) return 'is-quiet';
  // Map theme to Bulma background classes
  const themeMap = {
    primary: 'has-background-primary',
    danger: 'has-background-danger',
    warning: 'has-background-warning',
    info: 'has-background-info',
    success: 'has-background-success',
    dark: 'has-background-dark',
    light: 'has-background-light'
  };
  return themeMap[props.theme] || 'has-background-primary';
});

const titleClass = computed(() => {
  // White text for colored backgrounds, dark for light background
  if (props.quiet) return '';
  return props.theme === 'light' ? 'has-text-dark' : 'has-text-white';
});

const confirmButtonClass = computed(() => {
  // Match button class to theme
  return `is-${props.theme}`;
});

// Methods
const close = () => {
  emit('update:modelValue', false);
  emit('close');
};

const cancel = () => {
  emit('update:modelValue', false);
  emit('cancel');
};

const confirm = () => {
  emit('confirm');
  // Don't auto-close on confirm - let parent handle it
};

const handleBackgroundClick = () => {
  if (props.closeOnBackground && !props.loading) {
    close();
  }
};

// ESC key handler
const handleEscKey = (event) => {
  // In fullscreen, Esc belongs to the browser exiting fullscreen — don't also close the modal
  if (document.fullscreenElement || document.webkitFullscreenElement) return;
  if (event.key === 'Escape' && props.modelValue && props.closeOnEsc && !props.loading) {
    close();
  }
};

// Watch for modal open/close to manage body scroll
watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    document.body.classList.add('modal-open');
  } else {
    document.body.classList.remove('modal-open');
  }
});

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscKey);
  document.body.classList.remove('modal-open');
});
</script>

<style lang="scss" scoped>
// Ensure modal appears above other modals when teleported
.modal {
  z-index: 100;
}

// Ensure footer layout is consistent
.modal-card-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  
  .footer-left {
    display: flex;
    align-items: center;
  }
  
  .footer-right {
    display: flex;
    align-items: center;
    margin-left: auto;
    
    .button + .button {
      margin-left: 0.5rem;
    }
  }
}

// Remove border from colored headers
.modal-card-head {
  &.has-background-primary,
  &.has-background-danger,
  &.has-background-warning,
  &.has-background-info,
  &.has-background-success,
  &.has-background-dark {
    border-bottom: none;
  }
}

.modal-card-head-text {
  flex: 1;
  min-width: 0;
}

.modal-card-subtitle {
  margin-top: 0.1rem;
  font-size: 0.8rem;
  color: var(--bulma-text-weak);
}

// Quiet header: white, hairline, rounded card — the conversation-composer look
.modal-card.is-quiet {
  border-radius: var(--bulma-radius-large);
}

.modal-card-head.is-quiet {
  padding: 0.9rem 0.75rem 0.9rem 1.25rem;
  border-bottom: 1px solid var(--bulma-border-weak);
  border-radius: var(--bulma-radius-large) var(--bulma-radius-large) 0 0;
  background: var(--bulma-scheme-main-bis);
  box-shadow: none;

  .modal-card-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--bulma-text-strong);
  }

  & + .modal-card-body {
    padding: 1.1rem 1.25rem 1.25rem;
  }
}

.modal-card.is-quiet .modal-card-foot {
  padding: 0.75rem 1.25rem 1rem;
  border-top: none;
  background: var(--bulma-scheme-main);

  .button {
    font-size: var(--bulma-size-small);
  }
}

.modal-close-btn {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  kbd {
    display: inline-block;
    padding: 0.15rem 0.4rem;
    border: 1px solid var(--bulma-border);
    border-bottom-width: 2px;
    border-radius: var(--bulma-radius-small);
    background: var(--bulma-scheme-main);
    font-family: inherit;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--bulma-text-weak);
    transition: color 0.15s, border-color 0.15s;
  }

  &:hover kbd {
    color: var(--bulma-text-strong);
    border-color: var(--bulma-text-weak);
  }
}

// Size modifiers
.modal-card {
  &.is-small {
    width: 400px;
  }
  
  &.is-large {
    width: 90%;
    max-width: 960px;
  }
  
  &.is-fullscreen {
    width: 100%;
    height: 100%;
    max-width: 100%;
    margin: 0;
    
    .modal-card-body {
      flex: 1;
    }
  }
}

// Global style to prevent body scroll when modal is open
:global(body.modal-open) {
  overflow: hidden;
}
</style>